import { VestigingBeschikbareFaciliteiten, VestigingStatus } from "@/entities/vestiging/constants";
import { Vestiging } from "@/entities/vestiging/domain";
import { Badge, Checkbox, Icon, Label, Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components";
import { useIntervalRerender } from "@/shared/hooks";
import { cn } from "@/shared/utils";
import { FaciliteitenVariant } from "./faciliteiten-details";

const getFacilityIcon = (vestiging: Vestiging) => {
    const coffeeIcon = <Icon name="coffee" alt="koffie" className="text-content-emphasis" />;
    const toiletIcon = <Icon name="toilet" alt="toilet" className="text-content-emphasis" />;

    const hasCoffee = vestiging.beheerVestiging.beschikbareFaciliteiten.includes(VestigingBeschikbareFaciliteiten.KOFFIE_THEE);
    const hasToilet = vestiging.beheerVestiging.beschikbareFaciliteiten.includes(VestigingBeschikbareFaciliteiten.TOILET);

    // only one icon should be shown. Only dedicated toilets (where toilet is the only beschikbareFaciliteit) get the toilet icon.
    if (hasCoffee) return coffeeIcon;
    if (hasToilet) return toiletIcon;
    return undefined;
};

interface VestigingCheckboxProps {
    vestiging: Vestiging;
    isSelected: boolean;
    onSelectionChange: (vestigingId: number, selected: boolean) => void;
}

export function VestigingCheckbox({ vestiging, isSelected, onSelectionChange }: Readonly<VestigingCheckboxProps>) {
    const handleSelectedChange = (checkedState: boolean | "indeterminate") => {
        onSelectionChange(vestiging.beheerVestiging.id, !!checkedState);
    };

    return Vestiging.canRequestStart(vestiging) ? (
        <Checkbox checked={isSelected} onCheckedChange={handleSelectedChange} id={`facility-${vestiging.beheerVestiging.id}`} />
    ) : (
        <div className="size-4" />
    );
}

type NameColProps =
    | {
          variant: "nieuweAanvraagRegievoerder";
          vestiging: Vestiging;
          isSelected: boolean;
          onSelectionChange: (vestigingId: number, selected: boolean) => void;
      }
    | {
          variant: "bestaandeAanvraagRegievoerder" | "klantenservice";
          vestiging: Vestiging;
      };

export function NameCol(props: Readonly<NameColProps>) {
    const { variant, vestiging } = props;

    return (
        <div
            className={`flex items-center gap-2 ${variant === "nieuweAanvraagRegievoerder" && !Vestiging.canRequestStart(vestiging) ? "opacity-60" : ""}`}
        >
            {variant === "nieuweAanvraagRegievoerder" && (
                <VestigingCheckbox isSelected={props.isSelected} onSelectionChange={props.onSelectionChange} vestiging={vestiging} />
            )}
            <Label
                htmlFor={`facility-${vestiging.beheerVestiging.id}`}
                className={`text-content-default flex items-center ${Vestiging.canRequestStart(vestiging) ? "cursor-pointer" : "cursor-default"}`}
            >
                <span className="text-lg">{getFacilityIcon(vestiging)}</span>
                <span className="text-m font-normal">{Vestiging.getDisplayName(vestiging)}</span>
            </Label>
            {vestiging.beheerVestiging.isNsRetailVestiging &&
                (variant === "nieuweAanvraagRegievoerder" || variant === "bestaandeAanvraagRegievoerder") && (
                    <Icon name="more-ns" alt="NS logo" className="text-content-emphasis" />
                )}
            {vestiging.beheerVestiging.opmerkingVestiging && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Icon name="info" alt="info" className="text-content-emphasis" />
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>{vestiging.beheerVestiging.opmerkingVestiging}</p>
                    </TooltipContent>
                </Tooltip>
            )}
        </div>
    );
}

type OpeningHoursColProps = {
    variant?: FaciliteitenVariant;
    vestiging: Vestiging;
};

export function OpeningHoursCol({ variant, vestiging }: Readonly<OpeningHoursColProps>) {
    return (
        <span
            className={variant === "nieuweAanvraagRegievoerder" && !Vestiging.canRequestStart(vestiging) ? "opacity-60" : ""}
            data-testid="vestiging-openingstijden"
        >
            <span>{vestiging.currentOpeningstijden.openVan}</span> -{" "}
            <span className={cn(Vestiging.heeftAangepasteOpeningstijd(vestiging) && "font-bold")}>
                {vestiging.currentOpeningstijden.openTot}
            </span>
        </span>
    );
}

const badgeMap: Record<VestigingStatus, React.ReactElement> = {
    [VestigingStatus.BESTELD]: (
        <Badge variant="blue-subtle" className="text-xs">
            Besteld
        </Badge>
    ),
    [VestigingStatus.ACTIEF]: (
        <Badge variant="green-subtle" className="text-xs">
            Actief
        </Badge>
    ),
    [VestigingStatus.STOP_AANGEVRAAGD]: (
        <Badge variant="yellow-subtle" className="text-xs">
            Stop aangevraagd
        </Badge>
    ),
    [VestigingStatus.GESTOPT]: (
        <Badge variant="red-subtle" className="text-xs">
            Gestopt
        </Badge>
    ),
    [VestigingStatus.GAAT_NIET_LEVEREN]: (
        <Badge variant="red-subtle" className="text-xs">
            Gaat niet leveren
        </Badge>
    ),
};

type BadgeColProps = {
    variant?: FaciliteitenVariant;
    vestiging: Vestiging;
};

export function BadgeCol({ vestiging }: Readonly<BadgeColProps>) {
    useIntervalRerender(60); // re-render every minute so minutesUntilClose is live updating

    return (
        <div className="flex items-center justify-end gap-4 pr-0.5">
            {vestiging.aangevraagdeVestiging!.toelichtingKlantenservice && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Icon name="comment" alt="feedback klantenservice" className="text-content-emphasis" />
                    </TooltipTrigger>
                    <TooltipContent>
                        <p className="flex flex-col">
                            <span>Toelichting klantenservice:</span>
                            <span>"{vestiging.aangevraagdeVestiging!.toelichtingKlantenservice}"</span>
                        </p>
                    </TooltipContent>
                </Tooltip>
            )}
            {vestiging.aangevraagdeVestiging?.status ? (badgeMap[vestiging.aangevraagdeVestiging?.status] ?? null) : null}
        </div>
    );
}
